import React, { useState, useRef } from 'react';
import { api } from '../hooks/useApi';
import Icon from './Icon';

// ─── Whitelist ─────────────────────────────────────────────────────────────────
// Champs modifiables SANS raison obligatoire
const CHAMPS_AUTORISE = new Set([
  'operateurs', 'heure_debut', 'heure_fin', 'notes',
  'temp_conservation', 'saison', 'evenement_id',
]);

// Champs modifiables AVEC raison obligatoire
const CHAMPS_SOUS_CONDITION = new Set([
  'fournisseur_specifique', 'lot_specifique', 'lot_fournisseur',
  'date_achat_specifique', 'date_achat_matiere',
  'date_expiration_specifique', 'date_expiration_matiere',
  'date_congelation', 'date_expiration_produit',
]);

export function niveauChamp(champ) {
  if (CHAMPS_AUTORISE.has(champ)) return 'autorise';
  if (CHAMPS_SOUS_CONDITION.has(champ)) return 'sous_condition';
  return 'interdit';
}

// ─── Modale raison ─────────────────────────────────────────────────────────────
function ModalRaison({ champ, nouvelleValeur, batchId, onSuccess, onClose }) {
  const [raison, setRaison] = useState('');
  const [saving, setSaving] = useState(false);
  const [erreur, setErreur] = useState('');

  const submit = async () => {
    if (raison.trim().length < 10) {
      setErreur('La raison doit comporter au moins 10 caracteres.');
      return;
    }
    setSaving(true);
    setErreur('');
    try {
      await api.patch(`/batches/${batchId}/tracabilite-post-finalisation`, {
        champ,
        nouvelle_valeur: nouvelleValeur,
        raison: raison.trim(),
      });
      onSuccess(nouvelleValeur);
      onClose();
    } catch (e) {
      setErreur(e?.response?.data?.error || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-content"
        style={{ maxWidth: 480 }}
        onClick={e => e.stopPropagation()}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 'var(--space-5)' }}>
          <h3 style={{
            fontFamily: "'Cinzel', serif",
            fontSize: 14,
            fontWeight: 600,
            letterSpacing: '.06em',
            textTransform: 'uppercase',
            color: 'var(--accent)',
            margin: 0,
          }}>Raison de la modification</h3>
          <button className="btn btn-ghost btn--sm" onClick={onClose}>
            <Icon name="fermer" size={14} />
          </button>
        </div>

        <p className="muted" style={{ fontSize: 13, marginBottom: 'var(--space-3)' }}>
          Ce champ est sensible (tracabilite). Expliquez pourquoi vous modifiez{' '}
          <strong style={{ color: 'var(--accent)' }}>{champ}</strong> sur ce batch finalise.
        </p>
        <p style={{ fontSize: 13, marginBottom: 'var(--space-3)' }}>
          Nouvelle valeur :&nbsp;
          <span style={{ color: 'var(--text)', fontWeight: 600 }}>
            {nouvelleValeur || '(vide)'}
          </span>
        </p>

        <div className="field" style={{ marginBottom: 'var(--space-4)' }}>
          <label>Justification (minimum 10 caracteres)</label>
          <textarea
            rows={4}
            style={{ resize: 'vertical' }}
            placeholder="ex: Correction d'une faute de frappe dans le numero de lot. Lot reel : LOT-2026-0344."
            value={raison}
            onChange={e => { setRaison(e.target.value); setErreur(''); }}
            autoFocus
          />
        </div>

        {erreur && (
          <div className="error-inline" style={{ marginBottom: 'var(--space-3)' }}>
            <Icon name="erreur" size={14} /> {erreur}
          </div>
        )}

        <div style={{ display: 'flex', gap: 'var(--space-3)', justifyContent: 'flex-end' }}>
          <button className="btn btn-ghost" onClick={onClose} disabled={saving}>Annuler</button>
          <button
            className="btn btn-primary"
            onClick={submit}
            disabled={saving || raison.trim().length < 10}
          >
            {saving ? 'Sauvegarde...' : 'Confirmer la modification'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── ChampPostFin ──────────────────────────────────────────────────────────────
/**
 * Input intelligent pour les batches finalises.
 *
 * Props :
 *   batchId         : number
 *   champ           : string  — nom du champ SQL (ex: 'operateurs', 'lot_fournisseur')
 *   valeurInitiale  : string | null
 *   type            : 'text' | 'textarea' | 'date' | 'time' | 'select'
 *   options         : array [{value, label}] — uniquement si type='select'
 *   statut          : 'brouillon' | 'en_cours' | 'finalise'
 *   placeholder     : string (optionnel)
 *   onSuccess       : (nouvelleValeur) => void
 *   onChange        : (valeur) => void  — pour le mode non-finalise (parent gere)
 *   style           : CSSProperties (optionnel)
 */
export default function ChampPostFin({
  batchId,
  champ,
  valeurInitiale = '',
  type = 'text',
  options = [],
  statut,
  placeholder = '',
  onSuccess,
  onChange,
  style = {},
}) {
  const isFinalise = statut === 'finalise';
  const niveau = niveauChamp(champ);
  const [valeur, setValeur] = useState(valeurInitiale ?? '');
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState('');
  const [modalRaison, setModalRaison] = useState(false);
  const valeurRef = useRef(valeurInitiale ?? '');

  const showToast = (msg, ok = true) => {
    setToast({ msg, ok });
    setTimeout(() => setToast(''), 2800);
  };

  const handleChange = (val) => {
    setValeur(val);
    if (!isFinalise && onChange) onChange(val);
  };

  const patchDirectement = async (val) => {
    setSaving(true);
    try {
      await api.patch(`/batches/${batchId}/tracabilite-post-finalisation`, {
        champ,
        nouvelle_valeur: val,
      });
      showToast("Modifie et enregistre dans l'historique", true);
      valeurRef.current = val;
      if (onSuccess) onSuccess(val);
    } catch (e) {
      showToast(e?.response?.data?.error || 'Erreur lors de la sauvegarde', false);
      setValeur(valeurRef.current);
    } finally {
      setSaving(false);
    }
  };

  const handleBlur = () => {
    if (!isFinalise) return;
    const val = valeur ?? '';
    if (val === (valeurRef.current ?? '')) return;
    if (niveau === 'autorise') {
      patchDirectement(val);
    } else if (niveau === 'sous_condition') {
      setModalRaison(true);
    }
  };

  // Batch non finalise : input classique, le parent gere via onChange
  if (!isFinalise) {
    if (type === 'textarea') {
      return (
        <textarea
          rows={3}
          placeholder={placeholder}
          value={valeur}
          onChange={e => handleChange(e.target.value)}
          style={style}
        />
      );
    }
    if (type === 'select') {
      return (
        <select
          value={valeur}
          onChange={e => handleChange(e.target.value)}
          style={style}
        >
          {options.map(o => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      );
    }
    return (
      <input
        type={type === 'date' ? 'date' : type === 'time' ? 'time' : 'text'}
        placeholder={placeholder}
        value={valeur}
        onChange={e => handleChange(e.target.value)}
        style={style}
      />
    );
  }

  // ── Batch finalise ──

  // Champ interdit
  if (niveau === 'interdit') {
    const inputStyle = {
      opacity: .45,
      cursor: 'not-allowed',
      background: 'color-mix(in srgb, var(--danger) 8%, transparent)',
      ...style,
    };
    const badgeLock = (
      <span
        className="badge badge--danger"
        title="Ce champ est verrouille apres finalisation"
        style={{ marginLeft: 'var(--space-2)', verticalAlign: 'middle' }}
      >
        <Icon name="verrou" size={11} />
      </span>
    );
    if (type === 'textarea') {
      return (
        <div style={{ position: 'relative' }}>
          <textarea rows={3} value={valeur} disabled style={inputStyle} />
          {badgeLock}
        </div>
      );
    }
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
        <input type="text" value={valeur} disabled style={{ ...inputStyle, flex: 1 }} />
        {badgeLock}
      </div>
    );
  }

  // Champ autorise ou sous_condition : editable
  const inputEditable = (
    <>
      {toast && (
        <div style={{
          position: 'fixed',
          bottom: 24,
          right: 24,
          zIndex: 9999,
          background: toast.ok ? 'var(--success)' : 'var(--danger)',
          color: '#fff',
          padding: '10px 18px',
          borderRadius: 'var(--radius-md)',
          fontSize: 14,
          fontWeight: 600,
          boxShadow: 'var(--shadow-lg)',
          pointerEvents: 'none',
        }}>
          {toast.msg}
        </div>
      )}

      {type === 'textarea' ? (
        <textarea
          rows={3}
          placeholder={placeholder}
          value={valeur}
          onChange={e => handleChange(e.target.value)}
          onBlur={handleBlur}
          disabled={saving}
          style={{
            ...style,
            border: '1px solid color-mix(in srgb, var(--accent) 40%, transparent)',
            outline: 'none',
          }}
        />
      ) : type === 'select' ? (
        <select
          value={valeur}
          onChange={e => {
            handleChange(e.target.value);
            if (niveau === 'autorise') {
              patchDirectement(e.target.value);
            } else if (niveau === 'sous_condition') {
              setModalRaison(true);
            }
          }}
          disabled={saving}
          style={{
            ...style,
            border: '1px solid color-mix(in srgb, var(--accent) 40%, transparent)',
          }}
        >
          {options.map(o => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      ) : (
        <input
          type={type === 'date' ? 'date' : type === 'time' ? 'time' : 'text'}
          placeholder={placeholder}
          value={valeur}
          onChange={e => handleChange(e.target.value)}
          onBlur={handleBlur}
          disabled={saving}
          style={{
            ...style,
            border: '1px solid color-mix(in srgb, var(--accent) 40%, transparent)',
          }}
        />
      )}

      {saving && (
        <span style={{ fontSize: 11, color: 'var(--muted)', marginLeft: 6 }}>
          Sauvegarde...
        </span>
      )}

      {niveau === 'sous_condition' && (
        <span style={{ fontSize: 11, color: 'var(--warning)', display: 'block', marginTop: 3 }}>
          Modification soumise a justification
        </span>
      )}
    </>
  );

  return (
    <>
      {inputEditable}
      {modalRaison && (
        <ModalRaison
          champ={champ}
          nouvelleValeur={valeur}
          batchId={batchId}
          onSuccess={(val) => {
            valeurRef.current = val;
            showToast("Modifie et enregistre dans l'historique", true);
            if (onSuccess) onSuccess(val);
          }}
          onClose={() => {
            setValeur(valeurRef.current);
            setModalRaison(false);
          }}
        />
      )}
    </>
  );
}
